import React, { Component } from 'react';
import logger from '../../logger';
/**
 * Component that renders a video element for a passed in video track.
 *
 * @augments Component
 */
class Video extends Component {
    _videoElement;
    _mounted;
    _playPromise;
    /**
     * Default values for {@code Video} component's properties.
     *
     * @static
     */
    static defaultProps = {
        className: '',
        autoPlay: true,
        id: '',
        playsinline: true
    };
    /**
     * Initializes a new {@code Video} instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props) {
        super(props);
        /**
         * The internal reference to the DOM/HTML element intended for
         * displaying a video.
         *
         * @private
         * @type {HTMLVideoElement}
         */
        this._videoElement = null;
        this._playPromise = undefined;
        // Bind event handlers so they are only bound once for every instance.
        this._onVideoPlaying = this._onVideoPlaying.bind(this);
        this._setVideoElement = this._setVideoElement.bind(this);
    }
    /**
     * Invokes the library for rendering the video on initial display. Sets the
     * volume level to zero to ensure no sound plays.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidMount() {
        this._mounted = true;
        if (this._videoElement) {
            this._videoElement.volume = 0;
            this._videoElement.onplaying = this._onVideoPlaying;
        }
        this._attachTrack(this.props.videoTrack).finally(() => {
            if (this._videoElement && this.props.autoPlay) {
                // Ensure the video gets play() called on it. This may be necessary in the
                // case where the local video container was moved and re-attached, in which
                // case video does not autoplay.
                this._playPromise = this._videoElement.play()
                    .catch(error => {
                    // Prevent uncaught "DOMException: The play() request was interrupted by a new load request"
                    // This is a known browser issue when video tracks are rapidly replaced.
                    // It's safe to ignore this error when it's specifically about interruption.
                    const isInterruptedError = error?.name === 'AbortError'
                        || error?.message?.includes('interrupted')
                        || error?.message?.includes('play() request was interrupted');
                    if (this._mounted && !isInterruptedError) {
                        logger.warn(`Failed to play video element ${this.props.id}:`, error);
                    }
                    // Don't throw the error if it's about interruption - it's expected behavior
                    return Promise.resolve();
                })
                    .finally(() => {
                    this._playPromise = undefined;
                });
            }
        });
    }
    /**
     * Remove any existing associations between the current video track and the
     * component's video element.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentWillUnmount() {
        this._mounted = false;
        this._detachTrack(this.props.videoTrack);
    }
    /**
     * Updates the video display only if a new track is added. This component's
     * updating is blackboxed from React to prevent re-rendering of video
     * element, as the lib uses {@code track.attach(videoElement)} instead.
     *
     * @inheritdoc
     * @returns {boolean} - False is always returned to blackbox this component
     * from React.
     */
    shouldComponentUpdate(nextProps) {
        const currentJitsiTrack = this.props.videoTrack?.jitsiTrack;
        const nextJitsiTrack = nextProps.videoTrack?.jitsiTrack;
        if (currentJitsiTrack !== nextJitsiTrack) {
            // Cancel any pending play() promise before detaching
            // This prevents "play() request was interrupted" errors
            if (this._playPromise) {
                // The promise will be rejected when we detach, but we don't want to handle that error
                this._playPromise.catch(() => {
                    // Ignore - this is expected when tracks are replaced
                });
                this._playPromise = undefined;
            }
            this._detachTrack(this.props.videoTrack);
            this._attachTrack(nextProps.videoTrack).then(() => {
                // After attaching new track, call play() if autoplay is enabled
                if (this._videoElement && this.props.autoPlay && this._mounted) {
                    this._playPromise = this._videoElement.play()
                        .catch(error => {
                        const isInterruptedError = error?.name === 'AbortError'
                            || error?.message?.includes('interrupted')
                            || error?.message?.includes('play() request was interrupted');
                        if (this._mounted && !isInterruptedError) {
                            logger.warn(`Failed to play video element ${this.props.id}:`, error);
                        }
                        return Promise.resolve();
                    })
                        .finally(() => {
                        this._playPromise = undefined;
                    });
                }
            }).catch((_error) => {
                // Ignore the error. We are already logging it.
            });
        }
        if (this.props.style !== nextProps.style || this.props.className !== nextProps.className) {
            return true;
        }
        return false;
    }
    /**
     * Renders the video element.
     *
     * @override
     * @returns {ReactElement}
     */
    render() {
        const { autoPlay, className, id, muted, playsinline, style, eventHandlers } = this.props;
        return (React.createElement("video", { autoPlay: autoPlay, className: className, id: id, muted: muted, playsInline: playsinline, ref: this._setVideoElement, style: style, ...eventHandlers }));
    }
    /**
     * Calls into the passed in track to associate the track with the
     * component's video element and render video.
     *
     * @param {Object} videoTrack - The redux representation of the
     * {@code JitsiLocalTrack}.
     * @private
     * @returns {void}
     */
    _attachTrack(videoTrack) {
        const { id } = this.props;
        if (!videoTrack?.jitsiTrack) {
            logger.warn(`Attach is called on video element ${id} without tracks passed!`);
            console.warn('[VIDEO TRACK] Attach called without track:', { videoElementId: id });
            // returning Promise.resolve just keep the previous logic.
            // TODO: Check if it make sense to call play on this element or we can just return promise.reject().
            return Promise.resolve();
        }
        const trackId = videoTrack.jitsiTrack.getId?.();
        const participantId = videoTrack.jitsiTrack.getParticipantId?.();
        const isLocal = videoTrack.jitsiTrack.isLocal?.();
        const isMuted = videoTrack.jitsiTrack.isMuted?.();
        console.log('[VIDEO TRACK] Attaching track to video element:', {
            videoElementId: id,
            trackId,
            participantId,
            isLocal,
            isMuted,
            videoType: videoTrack.videoType,
            videoStarted: videoTrack.videoStarted
        });
        return videoTrack.jitsiTrack.attach(this._videoElement)
            .then(() => {
            console.log('[VIDEO TRACK] Track attached successfully:', {
                videoElementId: id,
                trackId,
                participantId
            });
        })
            .catch((error) => {
            logger.error(`Attaching the remote track ${videoTrack.jitsiTrack} to video with id ${id} has failed with `, error);
            console.error('[VIDEO TRACK] Track attachment failed:', {
                videoElementId: id,
                trackId,
                participantId,
                error: error.message,
                errorStack: error.stack
            });
        });
    }
    /**
     * Removes the association to the component's video element from the passed
     * in redux representation of jitsi video track to stop the track from
     * rendering.
     *
     * @param {Object} videoTrack -  The redux representation of the
     * {@code JitsiLocalTrack}.
     * @private
     * @returns {void}
     */
    _detachTrack(videoTrack) {
        if (this._videoElement && videoTrack && videoTrack.jitsiTrack) {
            videoTrack.jitsiTrack.detach(this._videoElement);
        }
    }
    /**
     * Invokes the onvideoplaying callback if defined.
     *
     * @private
     * @returns {void}
     */
    _onVideoPlaying() {
        if (this.props.onVideoPlaying) {
            this.props.onVideoPlaying();
        }
    }
    /**
     * Sets an instance variable for the component's video element so it can be
     * referenced later for attaching and detaching a JitsiLocalTrack.
     *
     * @param {Object} element - DOM element for the component's video display.
     * @private
     * @returns {void}
     */
    _setVideoElement(element) {
        this._videoElement = element;
    }
}
export default Video;
